import { required } from "../../shared/src/codecs/types/required";
import { TTypeOfCodec } from "../../shared/src/codecs/codec";
import { union } from "../../shared/src/codecs/types/union";
import { literal } from "../../shared/src/codecs/types/literal";
import { string } from "../../shared/src/codecs/types/string";
import { uuid } from "../../shared/src/codecs/types/uuid";
import { dateTime } from "../../shared/src/codecs/types/dateTime";
import { intersection } from "../../shared/src/codecs/types/intersection";
import { array } from "../../shared/src/codecs/types/array";
import { decimal } from "../../shared/src/codecs/types/decimal";
import { nullCodec } from "../../shared/src/codecs/types/nullCodec";

import { requiredFlatOverloaded } from "../../shared/src/codecs/types/requiredFlatOverloaded";
import { UsersIdChecksImageArray } from "./overloads/UsersIdChecksImage";
import { boolean } from "../../shared/src/codecs/types/boolean";

import { nonEmptyString } from "../../shared/src/codecs/types/nonEmptyString";
import { UsersIdCheckHasExpired } from "./overloads/UsersIdCheckHasExpired";
import { UsersIdCheckUploadedProofOfAddressUrl } from "./overloads/UsersIdCheckUploadedProofOfAddressUrl";
import { date } from "../../shared/src/codecs/types/date";

export const UsersIdCheckIdentityDocumentStatus = union([
    literal("unknown"),
    literal("pass"),
    literal("manual_pass"),
    literal("fail"),
]);
export type TUsersIdCheckIdentityDocumentStatus = TTypeOfCodec<typeof UsersIdCheckIdentityDocumentStatus>;

export const IdentityDocumentEstablished: Array<TUsersIdCheckIdentityDocumentStatus> = [
    "pass",
    "manual_pass",
];

export const UsersIdCheckAddressStatus = union([
    literal("unknown"),
    literal("pass"),
    literal("more_proof_required"),
    literal("fail"),
]);

export const UsersIdCheckPepsSanctionsStatus = union([
    literal("unknown"),
    literal("pass"),
    literal("requires_investigation"),
    literal("fail"),
]);

export const UsersIdCheck1 = required({
    id_check_result_identity_document_status: UsersIdCheckIdentityDocumentStatus,
});

export const UsersIdCheckAddress = required({
    id_check_result_address_status: UsersIdCheckAddressStatus,
});

export const UsersIdCheck2 = required({
    id_check_result_peps_sanctions_status: UsersIdCheckPepsSanctionsStatus,
});

export const UsersIdCheckStatuses = intersection([
    UsersIdCheck1,
    UsersIdCheckAddress,
    UsersIdCheck2,
]);

export const UsersIdCheckPotentialFraudOutcomeStatus = union([
    literal("unknown"),
    literal("not_fraud"),
    literal("is_fraud"),
]);
export const UsersIdCheck5 = required({
    potential_fraud_outcome: UsersIdCheckPotentialFraudOutcomeStatus,
});

export const UsersIdCheckManualPass = required({
    manual_pass_reason: string(),
});

export const UsersIdCheck3 = intersection([
    required({
        id: uuid(),
    }),
    UsersIdCheck1,
    UsersIdCheckAddress,
    UsersIdCheck2,
    UsersIdCheck5,
    UsersIdCheckManualPass,
]);
export type TUsersIdCheck3Codec = typeof UsersIdCheck3;

export const UsersIdCheckComplyAdvantageMatch = required({
    matching_name: string(),
    match_score: union([decimal(), nullCodec()]),
    aml_types: array(string()),
    date_of_birth: string(),
    country_codes: array(string()),
    image_url: union([string(), nullCodec()]),
    political_positions: array(string()),
    match_types: array(string()),
});
export type TUsersIdCheckComplyAdvantageMatchCodec = typeof UsersIdCheckComplyAdvantageMatch;
export type TUsersIdCheckComplyAdvantageMatch = TTypeOfCodec<TUsersIdCheckComplyAdvantageMatchCodec>;

export const UsersLegalDetailsAuditLogEntry = required({
    id: uuid(),
    user_id: uuid(),
    changed_by_user_id: uuid(),
    changed_by_user_name: string(),
    changed_by_user_type: string(),
    users_id_checks_id: union([nullCodec(), uuid()]),
    id_check_status_at_change: union([nullCodec(), string()]),
    field_name: string(),
    old_value: union([nullCodec(), string()]),
    new_value: union([nullCodec(), string()]),
    created_at: dateTime(),
});
export type TUsersLegalDetailsAuditLogEntryCodec = typeof UsersLegalDetailsAuditLogEntry;
export type TUsersLegalDetailsAuditLogEntry = TTypeOfCodec<TUsersLegalDetailsAuditLogEntryCodec>;

export const UsersIdCheck4 = intersection([
    required({
        users__cases_id: uuid(),
        id_check_result_peps_sanctions_report: string(),
        created_at: dateTime(),
        potential_fraud: boolean(),
        comply_advantage_matches: array(UsersIdCheckComplyAdvantageMatch),
        pep_declaration_signed_at: union([dateTime(), nullCodec()]),
    }),
    requiredFlatOverloaded({
        id_check_image_urls: UsersIdChecksImageArray,
    }),
    requiredFlatOverloaded({
        has_expired: UsersIdCheckHasExpired,
    }),
    requiredFlatOverloaded({
        uploaded_proof_of_address_url: UsersIdCheckUploadedProofOfAddressUrl,
    }),
    required({
        document_expiry_date: union([nullCodec(), date()]),
        identity_document_fail_reasons: array(string()),
        rescreened_from_id_check_id: union([nullCodec(), uuid()]),
        has_legal_name: boolean(),
        has_legal_dob: boolean(),
        has_correspondence_address: boolean(),
        id_check_awaiting_third_party_responses: boolean(),
        legal_details_audit: array(UsersLegalDetailsAuditLogEntry),
    }),
]);
export type TUsersIdCheck4Codec = typeof UsersIdCheck4;

export const UsersIdCheckPassed = required({
    id_check_result_identity_document_status: union([literal("pass"), literal("manual_pass")]),
    id_check_result_address_status: literal("pass"),
    id_check_result_peps_sanctions_status: literal("pass"),
});

export const UsersIdCheck7 = intersection([
    required({
        id: uuid(),
    }),
    UsersIdCheck1,
]);
export type TUsersIdCheck7Codec = typeof UsersIdCheck7;
export type TUsersIdCheck7 = TTypeOfCodec<TUsersIdCheck7Codec>;

export const UsersIdCheck8 = required({
    created_at: dateTime(),
    expires_at: dateTime(),
});
export type TUsersIdCheck8Codec = typeof UsersIdCheck8;
export type TUsersIdCheck8 = TTypeOfCodec<TUsersIdCheck8Codec>;
